import React from 'react';
import { DateTime } from 'luxon';

import { Text, Label } from '../../SubComponents/Text';
import useLocalize from '../../Hooks/useLocalize';
import { CalendarWrapper, CapacityIndicator, StyledCalendar } from './style';
import Loading from '../../SubComponents/Loading';
import { ReactComponent as Prev } from '../../Assets/Icons/prev.svg';
import { ReactComponent as Next } from '../../Assets/Icons/next.svg';
import useLocale from '../../Hooks/use-locale';
import { OpenDay } from '../../Hooks/use-open-days';

interface CalendarProps {
  selectedDate: Date | null;
  activeDate: Date | null;
  setActiveDate: (date: Date) => void;
  futureOpenDays: OpenDay[];
  isFutureDay: (date: Date) => boolean;
  getDay: (date: Date) => OpenDay | undefined;
  onChange: (date: Date) => void;
  timezoneName?: string;
  helpText?: string | null;
  openDaysError?: string | null;
  error?: string;
  enabled?: boolean;
  loading?: boolean;
  marginBottom?: boolean;
}

const Calendar = ({
  selectedDate,
  activeDate,
  setActiveDate,
  futureOpenDays,
  isFutureDay,
  getDay,
  onChange,
  timezoneName,
  helpText,
  openDaysError,
  error: externalError,
  enabled = true,
  loading,
  marginBottom = true,
}: CalendarProps) => {
  const { locale } = useLocale();
  const localize = useLocalize();

  return (
    <CalendarWrapper $marginBottom={marginBottom}>
      <Loading loading={loading} delay={300}>
        <StyledCalendar
          value={selectedDate}
          onChange={onChange}
          activeStartDate={activeDate ?? undefined}
          onActiveStartDateChange={({ activeStartDate }: { activeStartDate: Date }) =>
            setActiveDate(activeStartDate)
          }
          tileDisabled={({ date }: { date: Date }) => !enabled || !getDay(date)?.isOpen}
          locale={locale}
          minDetail="month"
          minDate={DateTime.now()
            .setZone(timezoneName)
            .setZone('local', { keepLocalTime: true })
            .startOf('day')
            .toJSDate()}
          showNeighboringMonth={false}
          tileContent={({ date }: { date: Date }) => (
            <CapacityIndicator
              capacity={getDay(date)?.capacity}
              disabled={!isFutureDay(date) || !getDay(date)?.isOpen}
            />
          )}
          prevLabel={enabled ? <Prev /> : null}
          nextLabel={enabled ? <Next /> : null}
          formatDay={(locale, date) => date.getDate().toString()}
        />
      </Loading>

      {openDaysError && (
        <Label error mt="3" fontStyle="italic">
          {openDaysError}
        </Label>
      )}

      {futureOpenDays.length < 1 && !loading ? (
        <Label mt="3" fontStyle="italic">
          {localize('product.noOpenDays')}
        </Label>
      ) : null}

      {externalError && (
        <Label error mt="3" fontStyle="italic">
          {externalError}
        </Label>
      )}

      {helpText && (
        <Text renderWhiteSpace mt="3">
          {helpText}
        </Text>
      )}
    </CalendarWrapper>
  );
};

export default Calendar;
